// src/utils/sat/satPlanData.js
//
// Firestore data layer for the `sat_plans` collection.
//
// Document model (one per generated plan):
//   {
//     studentDirId, diagnosticStudentId, sourceAttemptId, testId,
//     attemptLabel, sourceCompletedAtMs,
//     totalScore, rwScaledScore, mathScaledScore,
//     currentScore, targetScore, scoreGap,
//     totalHours, status: 'draft' | 'active' | 'archived',//
// A student may have SEVERAL active plans at once, provided their subjects are
// disjoint (see planMatch.js#plansConflict). Activating a plan only archives the
// other active plans that cover the same subjects.//     snapshot: { subjects, notTested, summary },     // frozen plan context
//     writtenPlan: { parentSummary, reasoning },
//     checklist: [ { id, subject, majorCategory, subcategory, key, accuracyPct,
//                    total, multiplier, rxHours, allocatedHours, isStretch,
//                    priority, subjectRank, guidance,
//                    status: 'todo'|'in_progress'|'done',
//                    completedByUid, completedByName, completedAt (ms),
//                    sessionLogId, progressNote } ],
//     aiProvider,
//     createdByUid, createdByName, createdAt, confirmedAt, updatedAt,
//     progressUpdatedAt, progressUpdatedByUid, progressUpdatedByName
//   }
//
// "Completed" is a DERIVED status (all required items done) — see
// derivePlanDisplayStatus — not a separate stored value.
//
// SECURITY: tutors may only update the keys
//   ['checklist','updatedAt','progressUpdatedAt','progressUpdatedByUid','progressUpdatedByName'].
// setChecklistItemStatus() touches only those keys. All other writers here are
// admin-only actions (allowed by the firestore.rules sat_plans block).

import { db } from "../../firebaseConfig";
import { planSubjectKeys, plansConflict } from "../plan/planMatch";
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";

const COL = "sat_plans";

// ── Soft delete ───────────────────────────────────────────────────────────────
// Tutors request deletion instead of deleting; the plan is flagged and hidden
// from tutors, and an admin can restore it for this many days before it is
// permanently purged.
export const DELETION_WINDOW_DAYS = 30;
const DELETION_WINDOW_MS = DELETION_WINDOW_DAYS * 24 * 60 * 60 * 1000;

/** True when a plan has been flagged for deletion (soft delete). */
export function isPendingDeletion(plan) {
  return !!plan?.deletionRequestedAt;
}

/** Epoch ms of a plan's deletion request (0 when not pending). */
export function deletionRequestedMs(plan) {
  return msOf(plan?.deletionRequestedAt);
}

/** Epoch ms when a pending deletion becomes permanent (0 when not pending). */
export function deletionExpiresMs(plan) {
  const at = deletionRequestedMs(plan);
  return at ? at + DELETION_WINDOW_MS : 0;
}

/** Whole days left in a pending deletion's restore window (>= 0). */
export function deletionDaysLeft(plan) {
  const expires = deletionExpiresMs(plan);
  if (!expires) return 0;
  return Math.max(0, Math.ceil((expires - Date.now()) / (24 * 60 * 60 * 1000)));
}

/** Resolve the student directory doc id from a linked diagnostic account uid. */
export async function findStudentDirIdByDiagnosticUid(diagUid) {
  if (!diagUid) return null;
  const snap = await getDocs(
    query(collection(db, "students"), where("diagnosticUid", "==", diagUid))
  );
  if (snap.empty) return null;
  return snap.docs[0].id;
}

/** Lightweight student list for the link-search UI (admin tool; lists are small). */
export async function fetchStudentsLite() {
  const snap = await getDocs(query(collection(db, "students"), orderBy("name", "asc")));
  return snap.docs.map((d) => {
    const v = d.data() || {};
    return { id: d.id, name: v.name || "", grade: v.grade ?? "", diagnosticUid: v.diagnosticUid || "" };
  });
}

/** Link a diagnostic account uid to an existing student directory record. */
export async function linkDiagnosticToStudent(studentDirId, diagUid) {
  await updateDoc(doc(db, "students", studentDirId), { diagnosticUid: diagUid, updatedAt: serverTimestamp() });
}

/**
 * Retire the student's other ACTIVE plans that COVER THE SAME SUBJECTS as the
 * plan being activated.
 *
 * A student may run several plans at once as long as they target different
 * subjects (an SAT Math plan alongside a Reading & Writing plan, or a custom
 * Chemistry plan beside an SAT plan), so only overlapping plans are archived.
 * A plan that declares no subjects can't be told apart from any other, so it
 * keeps the old stricter behaviour of retiring every other active plan.
 */
async function archiveConflictingActivePlans(studentDirId, exceptId = null, candidate = null) {
  if (!studentDirId) return;
  const prev = await getDocs(
    query(
      collection(db, COL),
      where("studentDirId", "==", studentDirId),
      where("status", "==", "active")
    )
  );
  const mine = planSubjectKeys(candidate);
  await Promise.all(
    prev.docs
      .filter((d) => d.id !== exceptId)
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((other) => !mine.size || plansConflict(candidate, other))
      .map((other) =>
        updateDoc(doc(db, COL, other.id), { status: "archived", updatedAt: serverTimestamp() })
      )
  );
}

/**
 * Create a new plan document.
 * status 'active' archives the previously-active plans that cover the same
 * subjects; plans for other subjects stay active.
 */
export async function createPlan(plan, actor = {}, { status = "active" } = {}) {
  if (status === "active") await archiveConflictingActivePlans(plan.studentDirId || "", null, plan);
  const ref = await addDoc(collection(db, COL), {
    ...plan,
    status,
    createdByUid: actor.uid || null,
    createdByName: actor.name || null,
    createdAt: serverTimestamp(),
    confirmedAt: status === "active" ? serverTimestamp() : null,
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

/** Confirm a reviewed plan as the active plan. */
export function confirmAndActivatePlan(plan, actor = {}) {
  return createPlan(plan, actor, { status: "active" });
}

/** Save a plan as a draft (not active). */
export function saveDraftPlan(plan, actor = {}) {
  return createPlan(plan, actor, { status: "draft" });
}

/** Update an existing plan's editable fields (admin edit). */
export async function savePlanEdits(planId, fields = {}) {
  await updateDoc(doc(db, COL, planId), { ...fields, updatedAt: serverTimestamp() });
}

/**
 * Make a plan active, retiring any other active plan that covers the same
 * subjects. Plans targeting different subjects are left active.
 */
export async function setActivePlan(studentDirId, planId) {
  const snap = await getDoc(doc(db, COL, planId));
  const plan = snap.exists() ? { id: snap.id, ...snap.data() } : null;
  await archiveConflictingActivePlans(studentDirId || plan?.studentDirId || "", planId, plan);
  await updateDoc(doc(db, COL, planId), {
    status: "active",
    confirmedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/** Change a plan's stored status (e.g., archive). */
export async function updatePlanStatus(planId, status) {
  await updateDoc(doc(db, COL, planId), { status, updatedAt: serverTimestamp() });
}

/** Permanently delete a plan. */
export async function deletePlan(planId) {
  await deleteDoc(doc(db, COL, planId));
}

/** Clear a pending deletion (admin restore). */
export async function restorePlan(planId) {
  await updateDoc(doc(db, COL, planId), {
    deletionRequestedAt: null,
    deletionRequestedByUid: null,
    deletionRequestedByName: null,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Soft-delete: flag a plan for deletion without removing it. Hidden from tutors
 * immediately; an admin can restore it for DELETION_WINDOW_DAYS.
 */
export async function requestPlanDeletion(planId, actor = {}) {
  await updateDoc(doc(db, COL, planId), {
    deletionRequestedAt: Date.now(),
    deletionRequestedByUid: actor.uid || null,
    deletionRequestedByName: actor.name || null,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Permanently delete plans whose restore window has elapsed. This is a lazy
 * sweep meant to run from an admin surface (Firestore delete is admin-only);
 * returns the number of plans purged.
 */
export async function purgeExpiredPlanDeletions(plans) {
  const now = Date.now();
  const expired = (plans || []).filter((p) => {
    const at = deletionRequestedMs(p);
    return at > 0 && now - at >= DELETION_WINDOW_MS;
  });
  await Promise.all(expired.map((p) => deletePlan(p.id)));
  return expired.length;
}

/** Duplicate a plan as a fresh draft with progress reset. */
export async function duplicatePlan(plan, actor = {}) {
  const checklist = (plan.checklist || []).map((it) => ({
    ...it,
    status: "todo",
    completedByUid: null,
    completedByName: null,
    completedAt: null,
    sessionLogId: null,
    progressNote: null,
  }));
  const {
    id,
    createdAt,
    confirmedAt,
    updatedAt,
    status,
    deletionRequestedAt,
    deletionRequestedByUid,
    deletionRequestedByName,
    ...rest
  } = plan;
  return createPlan({ ...rest, checklist }, actor, { status: "draft" });
}

export async function fetchPlanById(planId) {
  if (!planId) return null;
  const snap = await getDoc(doc(db, COL, planId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * All plans for a student directory, newest first.
 *
 * Soft-deleted ("pending deletion") plans are excluded by default so they
 * disappear for tutors; pass `{ includePending: true }` on an admin surface to
 * see them for restore / permanent delete.
 */
export async function fetchPlansForStudentDir(studentDirId, { includePending = false } = {}) {
  if (!studentDirId) return [];
  const snap = await getDocs(
    query(
      collection(db, COL),
      where("studentDirId", "==", studentDirId),
      orderBy("createdAt", "desc")
    )
  );
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((p) => includePending || !isPendingDeletion(p));
}

/** Every plan across all students, newest first (Plans hub). */
export async function fetchAllPlans({ includePending = false } = {}) {
  const snap = await getDocs(query(collection(db, COL), orderBy("createdAt", "desc")));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((p) => includePending || !isPendingDeletion(p));
}

/** Every active plan for a student directory, newest first (may be several). */
export async function fetchActivePlansForStudentDir(studentDirId) {
  if (!studentDirId) return [];
  const snap = await getDocs(
    query(
      collection(db, COL),
      where("studentDirId", "==", studentDirId),
      where("status", "==", "active")
    )
  );
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((p) => !isPendingDeletion(p))
    .sort((a, b) => msOf(b.createdAt) - msOf(a.createdAt));
}

/** The newest active plan for a student directory (convenience). */
export async function fetchActivePlanForStudentDir(studentDirId) {
  const list = await fetchActivePlansForStudentDir(studentDirId);
  return list[0] || null;
}

/** Every active plan for a diagnostic uid, newest first (may be several). */
export async function fetchActivePlansForDiagnosticUid(diagUid) {
  if (!diagUid) return [];
  const snap = await getDocs(
    query(
      collection(db, COL),
      where("diagnosticStudentId", "==", diagUid),
      where("status", "==", "active")
    )
  );
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((p) => !isPendingDeletion(p))
    .sort((a, b) => msOf(b.createdAt) - msOf(a.createdAt));
}

/** The newest active plan for a diagnostic uid (convenience fallback). */
export async function fetchActivePlanForDiagnosticUid(diagUid) {
  const list = await fetchActivePlansForDiagnosticUid(diagUid);
  return list[0] || null;
}

/**
 * Update a single checklist item's status + progress attribution.
 * Only touches the keys tutors are allowed to write.
 */
export async function setChecklistItemStatus({
  planId,
  itemId,
  status,
  actorUid,
  actorName,
  sessionLogId,
  note,
}) {
  const ref = doc(db, COL, planId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("SAT plan not found.");

  const data = snap.data() || {};
  const done = status === "done";

  const checklist = (data.checklist || []).map((it) => {
    if (it.id !== itemId) return it;
    return {
      ...it,
      status,
      completedByUid: done ? actorUid || null : it.completedByUid || null,
      completedByName: done ? actorName || null : it.completedByName || null,
      completedAt: done ? Date.now() : status === "todo" ? null : it.completedAt || null,
      sessionLogId: sessionLogId || it.sessionLogId || null,
      progressNote: note != null ? note : it.progressNote || null,
    };
  });

  await updateDoc(ref, {
    checklist,
    updatedAt: serverTimestamp(),
    progressUpdatedAt: serverTimestamp(),
    progressUpdatedByUid: actorUid || null,
    progressUpdatedByName: actorName || null,
  });

  return checklist;
}

function clampPct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

/** Status derived purely from completion percentage. */
export function deriveStatusFromPct(pct) {
  const p = clampPct(pct);
  if (p >= 100) return "done";
  if (p > 0) return "in_progress";
  return "todo";
}

/** Completion % for an item — uses progressPct, falling back to legacy status. */
export function itemProgressPct(it) {
  if (it && it.progressPct != null) return clampPct(it.progressPct);
  if (it && it.status === "done") return 100;
  if (it && it.status === "in_progress") return 50;
  return 0;
}

/**
 * Record measurable progress on a plan item from a session.
 * Adds `hoursWorked` to the item's covered hours, sets the completion %, derives
 * the status, and appends a progress-history entry. Only touches the keys
 * tutors are allowed to write (checklist + progress* + updatedAt).
 */
export async function recordItemProgress({
  planId,
  itemId,
  hoursWorked = 0,
  progressPct,
  actorUid,
  actorName,
  sessionLogId,
}) {
  const ref = doc(db, COL, planId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("SAT plan not found.");
  const data = snap.data() || {};

  const worked = Math.max(0, Number(hoursWorked) || 0);
  const now = Date.now();

  const checklist = (data.checklist || []).map((it) => {
    if (it.id !== itemId) return it;
    const prevCovered = Number(it.coveredHours) || 0;
    const coveredHours = Math.round((prevCovered + worked) * 100) / 100;
    const pct = progressPct == null ? itemProgressPct(it) : clampPct(progressPct);
    const status = deriveStatusFromPct(pct);
    const done = status === "done";
    const entry = {
      at: now,
      tutorUid: actorUid || null,
      tutorName: actorName || null,
      hoursWorked: worked,
      progressPct: pct,
      sessionLogId: sessionLogId || null,
    };
    return {
      ...it,
      coveredHours,
      progressPct: pct,
      status,
      completedByUid: done ? actorUid || it.completedByUid || null : it.completedByUid || null,
      completedByName: done ? actorName || it.completedByName || null : it.completedByName || null,
      completedAt: done ? it.completedAt || now : it.completedAt || null,
      sessionLogId: sessionLogId || it.sessionLogId || null,
      progressHistory: [...(Array.isArray(it.progressHistory) ? it.progressHistory : []), entry],
    };
  });

  await updateDoc(ref, {
    checklist,
    updatedAt: serverTimestamp(),
    progressUpdatedAt: serverTimestamp(),
    progressUpdatedByUid: actorUid || null,
    progressUpdatedByName: actorName || null,
  });
  return checklist;
}

/**
 * Idempotent per-session progress update — safe for debounced auto-save.
 * Keeps ONE history entry per (item, sessionLogId): repeated calls update that
 * entry rather than accumulating. Covered hours = sum of all history entries'
 * hours (so a session's contribution can be edited freely, and total worked
 * hours may exceed planned hours). Completion % is the latest recorded value.
 */
export async function upsertItemSessionProgress({
  planId,
  itemId,
  sessionLogId,
  sessionHours = 0,
  progressPct,
  actorUid,
  actorName,
}) {
  const ref = doc(db, COL, planId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("SAT plan not found.");
  const data = snap.data() || {};
  const worked = Math.max(0, Number(sessionHours) || 0);
  const now = Date.now();

  const checklist = (data.checklist || []).map((it) => {
    if (it.id !== itemId) return it;
    const history = Array.isArray(it.progressHistory) ? [...it.progressHistory] : [];
    const pct = clampPct(progressPct == null ? itemProgressPct(it) : progressPct);
    const entry = {
      at: now,
      tutorUid: actorUid || null,
      tutorName: actorName || null,
      hoursWorked: worked,
      progressPct: pct,
      sessionLogId: sessionLogId || null,
    };
    const idx = sessionLogId ? history.findIndex((h) => h.sessionLogId === sessionLogId) : -1;
    if (idx >= 0) history[idx] = entry;
    else history.push(entry);

    const coveredHours = Math.round(history.reduce((a, h) => a + (Number(h.hoursWorked) || 0), 0) * 100) / 100;
    const status = deriveStatusFromPct(pct);
    const done = status === "done";
    return {
      ...it,
      coveredHours,
      progressPct: pct,
      status,
      completedByUid: done ? actorUid || it.completedByUid || null : it.completedByUid || null,
      completedByName: done ? actorName || it.completedByName || null : it.completedByName || null,
      completedAt: done ? it.completedAt || now : it.completedAt || null,
      sessionLogId: sessionLogId || it.sessionLogId || null,
      progressHistory: history,
    };
  });

  await updateDoc(ref, {
    checklist,
    updatedAt: serverTimestamp(),
    progressUpdatedAt: serverTimestamp(),
    progressUpdatedByUid: actorUid || null,
    progressUpdatedByName: actorName || null,
  });
  return checklist;
}

/** Progress rollup used by the admin tab, cards, and tutor views. */
export function summarizePlanProgress(plan) {
  const checklist = (plan && plan.checklist) || [];
  const required = checklist.filter((it) => !it.isStretch);
  const plannedHours = required.reduce((a, b) => a + (Number(b.allocatedHours) || 0), 0);
  const coveredHours = required.reduce((a, b) => a + (Number(b.coveredHours) || 0), 0);

  const doneCount = required.filter((it) => itemProgressPct(it) >= 100).length;
  const inProgressCount = required.filter((it) => {
    const p = itemProgressPct(it);
    return p > 0 && p < 100;
  }).length;

  let percent;
  if (plannedHours > 0) {
    percent = Math.round(
      required.reduce((a, b) => a + (Number(b.allocatedHours) || 0) * itemProgressPct(b), 0) / plannedHours
    );
  } else {
    percent = required.length
      ? Math.round(required.reduce((a, b) => a + itemProgressPct(b), 0) / required.length)
      : 0;
  }

  return {
    requiredCount: required.length,
    doneCount,
    inProgressCount,
    remainingCount: required.length - doneCount,
    plannedHours,
    coveredHours,
    remainingHours: Math.max(0, Math.round((plannedHours - coveredHours) * 100) / 100),
    // legacy aliases (kept so existing consumers don't break)
    requiredHours: plannedHours,
    doneHours: coveredHours,
    percent,
  };
}

/** Display status: 'completed' when an active plan has all required items done. */
export function derivePlanDisplayStatus(plan) {
  const stored = plan?.status || "draft";
  if (stored === "active") {
    const p = summarizePlanProgress(plan);
    if (p.requiredCount > 0 && p.doneCount === p.requiredCount) return "completed";
  }
  return stored;
}

function msOf(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (typeof ts.toDate === "function") return ts.toDate().getTime();
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

export function tsToMs(ts) {
  return msOf(ts);
}
